import React from 'react';

interface LandingCardProps {
  children: React.ReactNode;
  variant?: 'default' | 'elevated' | 'outlined' | 'filled';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  className?: string;
  hover?: boolean;
  onClick?: () => void;
}

const variantStyles = {
  default: 'bg-white border-2 border-neutral-200',
  elevated: 'bg-white border-2 border-neutral-900 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]',
  outlined: 'bg-transparent border-2 border-neutral-900',
  filled: 'bg-neutral-100 border-2 border-neutral-200',
};

const paddingStyles = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

export function LandingCard({
  children,
  variant = 'default',
  padding = 'md',
  className = '',
  hover = false,
  onClick,
}: LandingCardProps) {
  const hoverStyles = hover
    ? 'cursor-pointer hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-1 transition-all duration-200'
    : '';

  return (
    <div
      onClick={onClick}
      className={`rounded-xl ${variantStyles[variant]} ${paddingStyles[padding]} ${hoverStyles} ${className}`}
    >
      {children}
    </div>
  );
}

interface FeatureCardProps {
  title: string;
  description: string;
  image?: string;
  icon?: React.ReactNode;
  href?: string;
  accentColor?: string;
}

export function FeatureCard({
  title,
  description,
  image,
  icon,
  href,
  accentColor = 'bg-blue-500',
}: FeatureCardProps) {
  const content = (
    <>
      <div className="h-40 w-full bg-neutral-50 relative overflow-hidden">
        {image ? (
          <img src={image} alt={title} className="w-full h-full object-cover" />
        ) : icon ? (
          <div className={`absolute inset-0 opacity-10 ${accentColor}`} />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-neutral-300">
            Preview
          </div>
        )}
      </div>

      <div className="p-6">
        <h3 className="text-xl font-bold mb-2 text-neutral-900">{title}</h3>
        <p className="text-neutral-600 text-sm leading-relaxed">{description}</p>

        <div className="mt-4 pt-4 border-t border-neutral-100 flex items-center text-sm font-semibold text-neutral-500 group-hover:text-neutral-900 transition-colors">
          Pelajari lebih lanjut
          <ArrowRight size={14} className="ml-2" />
        </div>
      </div>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        className="block bg-white border-2 border-neutral-200 rounded-xl overflow-hidden hover:border-neutral-900 hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-1 transition-all duration-200 group"
      >
        {content}
      </a>
    );
  }

  return (
    <div className="bg-white border-2 border-neutral-200 rounded-xl overflow-hidden">{content}</div>
  );
}

function ArrowRight({ size, className }: { size: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  );
}
